import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import {
  FiSearch,
  FiRefreshCw,
  FiLayers,
  FiAlertTriangle,
  FiCalendar,
  FiEdit2,
  FiZap,
} from "react-icons/fi";
import { fetchTaskPool } from "../../../utils/taskApi";
import {
  TASK_TYPE_LABEL,
  TASK_TYPE_BADGE,
  PLATFORM_LABEL,
  PLATFORM_BADGE,
  POOL_STATUS_LABEL,
  POOL_STATUS_BADGE,
  formatWhen,
} from "../taskConstants";

/**
 * The Pool tab — the admin's stock, and how long it lasts.
 *
 * The screen exists for one number. `days_of_runway` answers "does my free
 * afternoon need spending on task entry?", and it is deliberately the largest
 * thing on the page. Everything else is there to explain it: a pool of 23 with
 * 2 eligible today is not a healthy pool, it is a pool where everything is
 * inside its cooldown, and the two figures sitting side by side are what make
 * that visible.
 */

const StatCard = ({ icon: Icon, label, value, hint, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-50 text-gray-600",
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
        <p className="text-xl font-bold text-gray-800 leading-tight mt-0.5">{value}</p>
        {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
};

const PoolTab = ({ onEditTask, onPinTask, reloadKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchTaskPool({
        page,
        limit: 20,
        search: debouncedSearch,
        status: statusFilter,
        taskType: typeFilter,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setLastPage(res.pagination?.total_pages || 1);
      setTotal(res.pagination?.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load the pool");
      toast.error(err.message || "Failed to load the pool");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(val);
    }, 400);
  };

  // Two different alarms, and they are not the same problem. An empty pool
  // needs new tasks written; a full pool with nothing eligible needs cooldowns
  // or campaign windows looked at. Saying which one it is saves the admin the
  // guess.
  const runway = summary?.days_of_runway ?? null;
  const lowRunway = summary && runway !== null && runway <= 2;
  const nothingEligible = summary && summary.ready_tasks > 0 && summary.eligible_today === 0;

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={FiCalendar}
            label="Runway"
            value={runway === 0 ? "Out of stock" : `${runway} ${runway === 1 ? "day" : "days"}`}
            hint={`at ${summary.daily_count} a day`}
            tone={lowRunway ? "rose" : "emerald"}
          />
          <StatCard
            icon={FiLayers}
            label="Ready tasks"
            value={summary.ready_tasks}
            hint="in the pool, campaign window open"
            tone="blue"
          />
          <StatCard
            icon={FiZap}
            label="Eligible today"
            value={summary.eligible_today}
            hint="what the generator could pick right now"
            tone={nothingEligible ? "amber" : "gray"}
          />
          <StatCard
            icon={FiCalendar}
            label="Scheduled ahead"
            value={summary.scheduled_ahead}
            hint={summary.is_enabled ? "engine is on" : "engine is off"}
            tone={summary.is_enabled ? "gray" : "amber"}
          />
        </div>
      )}

      {lowRunway && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3">
          <FiAlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm">
            <p className="font-semibold text-rose-800">
              {runway === 0 ? "The pool is empty." : `Only ${runway} ${runway === 1 ? "day" : "days"} of tasks left.`}
            </p>
            <p className="text-rose-700 text-xs mt-1">
              At {summary.daily_count} a day, the schedule runs dry{runway === 0 ? " now" : ` in ${runway} ${runway === 1 ? "day" : "days"}`}. Add
              tasks and set them to <span className="font-semibold">Ready</span> to restock.
            </p>
          </div>
        </div>
      )}

      {!lowRunway && nothingEligible && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">
              {summary.ready_tasks} ready tasks, but none can run today.
            </p>
            <p className="text-amber-700 text-xs mt-1">
              They are inside their cooldown, past their campaign window, or already at their completion cap.
              A task with no cooldown set runs <span className="font-semibold">once only</span>.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            value={search}
            onChange={handleSearchChange}
            placeholder="Search the pool..."
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          <option value="">All Pool Status</option>
          {Object.entries(POOL_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          <option value="">All Types</option>
          {Object.entries(TASK_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button
          onClick={load}
          disabled={loading}
          className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg transition"
          title="Refresh"
        >
          <FiRefreshCw size={15} className={loading ? "animate-spin text-blue-600" : ""} />
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                <th className="px-5 py-3.5">Title</th>
                <th className="px-5 py-3.5">Type</th>
                <th className="px-5 py-3.5">Platform</th>
                <th className="px-5 py-3.5">Points</th>
                <th className="px-5 py-3.5">Pool Status</th>
                <th className="px-5 py-3.5">Weight</th>
                <th className="px-5 py-3.5">Cooldown</th>
                <th className="px-5 py-3.5">Last Run</th>
                <th className="px-5 py-3.5">Published</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs">
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-5 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <FiRefreshCw className="animate-spin text-blue-600" size={20} />
                      <span>Loading the pool...</span>
                    </div>
                  </td>
                </tr>
              ) : error && rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FiAlertTriangle size={28} className="text-rose-300" />
                      <p className="font-medium text-gray-600">{error}</p>
                      <button onClick={load} className="text-xs text-blue-600 hover:underline mt-1">
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-5 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <FiLayers size={28} className="text-gray-300" />
                      <p className="font-medium text-gray-500">Nothing in the pool</p>
                      <p className="text-xs text-gray-400">
                        Add tasks on the Tasks tab and set them to Ready — the scheduler publishes from here.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50/70 transition">
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-gray-800">{task.title}</p>
                      {(task.valid_from || task.valid_until) && (
                        <p className="text-[11px] text-gray-400">
                          {task.valid_from || "—"} → {task.valid_until || "—"}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${TASK_TYPE_BADGE}`}>
                        {TASK_TYPE_LABEL[task.task_type] || task.task_type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${
                          PLATFORM_BADGE[task.platform] || PLATFORM_BADGE.both
                        }`}
                      >
                        {PLATFORM_LABEL[task.platform] || task.platform}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-700 font-medium">{task.reward_points}</td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${
                          POOL_STATUS_BADGE[task.status] || POOL_STATUS_BADGE.draft
                        }`}
                      >
                        {POOL_STATUS_LABEL[task.status] || task.status}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-700">{task.weight}</td>
                    <td className="px-5 py-3.5 text-gray-500">
                      {task.cooldown_days ? (
                        `${task.cooldown_days}d`
                      ) : (
                        <span title="With no cooldown set, a task runs once and never repeats.">Once only</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500">{formatWhen(task.last_scheduled_at)}</td>
                    <td className="px-5 py-3.5 text-gray-700">
                      {task.occurrence_count}
                      {task.max_completions ? (
                        <span className="text-gray-400"> · cap {task.max_completions}</span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onPinTask && onPinTask(task)}
                          title="Pin this task onto a day"
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition"
                        >
                          <FiCalendar size={16} />
                        </button>
                        <button
                          onClick={() => onEditTask && onEditTask(task)}
                          title="Edit task"
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                        >
                          <FiEdit2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {lastPage > 1 && (
          <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <span>
              Showing {rows.length} of {total} tasks
            </span>
            <div className="flex items-center gap-1.5">
              <button
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Previous
              </button>
              <span className="px-2 font-medium">
                Page {page} of {lastPage}
              </span>
              <button
                disabled={page >= lastPage || loading}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PoolTab;
